import { useCallback, useState, useImperativeHandle, forwardRef } from 'react'
import {
  Upload,
  UploadFileInfo,
  UploadOnRemoveEvent,
  UploadOnAddEvent,
  UploadOnStatusChangeEvent,
} from '@progress/kendo-react-upload'
import styled from 'styled-components'
import { loadMessages, LocalizationProvider } from '@progress/kendo-react-intl'
import { IntlProvider } from '@progress/kendo-react-intl'
import trMessages from './tr-translate'
import arMessages from './ar-translate'
import { useTranslation } from 'react-i18next'
import { useAppSelector } from 'app/hooks'
import { BUTTONS, ERRORS, OTHERS } from 'locales/locales'
import CustomText from 'components/text/CustomText'
import useUploadPhoto from 'utils/hooks/useUploadPhoto'
import DropdownAlert from 'components/dropdown-alert/DropdownAlert'
import { useLogoUrl } from 'utils/hooks/useLogoUrl'

loadMessages(trMessages, 'tr-TR')
loadMessages(arMessages, 'ar-AR')

interface UploaderProps {
  fileUrl: string
  onFileUpload?: (url: string) => void
  type: string
  id?: string
  manualUpload?: boolean
  restrictions?: {
    allowedExtensions?: string[]
    maxFileSize?: number
  }
  accept?: string
}

export interface UploaderRef {
  performUpload: () => Promise<void>
  hasSelectedFile: () => boolean
}

const Uploader = forwardRef<UploaderRef, UploaderProps>(({ fileUrl, onFileUpload, type, manualUpload = false, restrictions, accept }, ref) => {
  const [files, setFiles] = useState<Array<UploadFileInfo>>([])
  const [uploadedFile, setUploadedFile] = useState<File | string | null>(fileUrl)
  const [selectedFile, setSelectedFile] = useState<File | null>(null)
  const notificationLanguage =
    useAppSelector((state) => state.auth.user.notificationLanguage) || 'tr'
  const { t } = useTranslation()

  const onUploadReturnUrl = (url: string) => {
    setUploadedFile(url)
    onFileUpload?.(url)
  }

  const { isUploadLoading, onUploadPhoto, uploadToServer } = useUploadPhoto(onUploadReturnUrl, type)

  // Manuel yükleme fonksiyonu
  const performUpload = useCallback(async () => {
    if (selectedFile && manualUpload) {
      const fileList = new DataTransfer()
      fileList.items.add(selectedFile)
      await uploadToServer(fileList.files[0])
    } else {
      // console.log('Upload şartları sağlanmadı', { 
      //   hasSelectedFile: !!selectedFile, 
      //   isManualUpload: manualUpload 
      // })
    }
  }, [selectedFile, manualUpload, uploadToServer])

  // Ref ile dışarıdan erişilebilir fonksiyonlar
  useImperativeHandle(ref, () => ({
    performUpload,
    hasSelectedFile: () => !!selectedFile
  }), [performUpload, selectedFile])


  const onAdd = useCallback(
    (event: UploadOnAddEvent) => {
      const file = (event?.affectedFiles[0] as { getRawFile: () => File })?.getRawFile()
      if (!['image/jpeg', 'image/png', 'image/jpg'].includes(file?.type)) {
        DropdownAlert.ref.show({
          isError: true,
          message: t(ERRORS.UNSUPPORTED_FILE_TYPE_ERROR, { file_name: file?.name }),
        })
        return
      }
      if (!file) {
        DropdownAlert.ref.show({ isError: true, message: t(ERRORS.SOMETHING_WENT_WRONG_ERROR) })
        return
      }

      if (manualUpload) {
        // Manuel yükleme modunda sadece dosyayı sakla ve preview göster
        setFiles(event.newState)
        setSelectedFile(file)
        const objectUrl = URL.createObjectURL(file)
        setUploadedFile(objectUrl)
      } else {
        // Otomatik yükleme modunda hemen yükle
        setFiles(event.newState)
        const fileList = new DataTransfer()
        fileList.items.add(file)
        onUploadPhoto(fileList.files)
      }
    },
    [manualUpload, onUploadPhoto],
  )

  const onRemove = useCallback(
    (event: UploadOnRemoveEvent) => {
      setFiles(event.newState)
      setSelectedFile(null)
      setUploadedFile(fileUrl || null)
    },
    [fileUrl],
  )

  const onStatusChange = useCallback((event: UploadOnStatusChangeEvent) => {
    setFiles(event.newState)
  }, [])

/*   useEffect(() => {
    if (fileInfo?.blobUrl) {
      setUploadedFile(fileInfo.blobUrl)
    }
  }, [fileInfo]) */

  const { logoUrl: logoUrl, logoRef: logoRef } = useLogoUrl(uploadedFile as string)

  return (
    <SettingsSection>
      <FileUploadContainer>
        <FileUploadArea>
          <LocalizationProvider
            language={
              notificationLanguage === 'tr'
                ? `${notificationLanguage}-${notificationLanguage?.toUpperCase()}`
                : notificationLanguage === 'ar'
                ? `${notificationLanguage}-${notificationLanguage?.toUpperCase()}`
                : 'en-US'
            }
          >
            <IntlProvider locale={notificationLanguage}>
              <Upload
                selectMessageUI={() => <CustomText>{t(BUTTONS.UPLOAD)}</CustomText>}
                listItemUI={(props) => <> {props.files.map((file) => file.name)} </>}
                batch={false}
                multiple={false}
                files={files}
                onAdd={onAdd}
                restrictions={restrictions}
                autoUpload={false}
                showActionButtons={false}
                onRemove={onRemove}
                onStatusChange={onStatusChange}
                withCredentials={true}
                showFileList={true}
                accept={accept}
              />
            </IntlProvider>
          </LocalizationProvider>
        </FileUploadArea>
        <FilePreviewArea>
          {isUploadLoading ? (
            <div>{t(OTHERS.LOADING)}</div>
          ) : logoUrl ? (
            <img
                src={logoUrl}
              ref={logoRef}
            />
          ) : null}
        </FilePreviewArea>
      </FileUploadContainer>
    </SettingsSection>
  )
})

Uploader.displayName = 'Uploader'

export default Uploader

const SettingsSection = styled.div`
  flex: 1;
  max-width: 350px;
`

const FileUploadContainer = styled.div`
  border: 1px solid #e3ebf6;
  border-radius: 4px;
  overflow: hidden;
  width: 100%;
`

const FileUploadArea = styled.div`
  padding: 12px;
  background-color: #f9fbfd;
  border-bottom: 1px solid #e3ebf6;
`

const FilePreviewArea = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 80px;
  width: 100%;
  padding: 10px;
  img {
    max-height: 80px;
    max-width: 100px;
    object-fit: scale-down;
  }
`
